import {
  AccessibilityNewOutlined,
  AccountCircleOutlined,
  BadgeOutlined,
  CheckroomOutlined,
  FaceOutlined,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedActor,
  updateActor,
} from "../../app/slices/TrainingSlice";
import PiInputfield from "../../components/PiInputField";
import PiSegmentedToggle, {
  PiSegmentedToggleOption,
} from "../../components/PiSegmentedToggle";
import {
  Actor,
  Gender,
  getDefaultRandomInGameNameType,
  InGameNameMode,
  RandomInGameNameType,
} from "../../models/Actor";
import ActorCharacterPresetSelector from "./ActorCharacterPresetSelector";
import ActorOutfitSelector from "./ActorOutfitSelector";
import ActorProfilePictureSelector from "./ActorProfilePictureSelector";
import "./ActorPresetEditor.css";
import ActorPresetEditorField from "./ActorPresetEditorField";
import PiButton from "../../components/PiButton";
import useViewport3D from "../viewport3d/Viewport3D.hook";
import PiSeperator from "../previews/components/PiSeperator";
import PiCreatorStringDropdown from "../../components/PiCreatorDropdown/PiCreatorStringDropdown";

export default function ActorPresetEditor(props: { isQuickAction?: boolean }) {
  const { isQuickAction } = props;
  const selectedActor = useSelector(selectSelectedActor);
  const isPlayer = selectedActor?.id === "player";
  const dispatch = useDispatch();
  const [workingName, setWorkingName] = useState<string>(
    selectedActor?.workingName ?? ""
  );
  const [inGameName, setInGameName] = useState<string>(
    selectedActor?.inGameName ?? selectedActor?.workingName ?? ""
  );
  const [inGameNameMode, setInGameNameMode] = useState<InGameNameMode>(
    selectedActor?.inGameNameMode ?? InGameNameMode.manual
  );
  const [randomInGameNameType, setRandomInGameNameType] =
    useState<RandomInGameNameType>(
      selectedActor?.randomInGameNameType ??
        getDefaultRandomInGameNameType(selectedActor?.gender)
    );
  const { saveQuickActorModal, cancelQuickActorModal } = useViewport3D();

  useEffect(() => {
    setWorkingName(selectedActor?.workingName ?? "");
    setInGameName(selectedActor?.inGameName ?? selectedActor?.workingName ?? "");
    setInGameNameMode(
      selectedActor?.inGameNameMode ?? InGameNameMode.manual
    );
    setRandomInGameNameType(
      selectedActor?.randomInGameNameType ??
        getDefaultRandomInGameNameType(selectedActor?.gender)
    );
  }, [selectedActor]);

  const updateSelectedActor = (updates: Partial<Actor>) => {
    if (!selectedActor) return;

    dispatch(
      updateActor({
        ...selectedActor,
        ...updates,
      })
    );
  };

  const onChangeWorkingName = (newName: string) => {
    if (!selectedActor) return;
    const shouldSyncInGameName =
      (selectedActor.inGameNameMode ?? InGameNameMode.manual) ===
        InGameNameMode.manual &&
      (selectedActor.inGameName ?? selectedActor.workingName) ===
        selectedActor.workingName;

    setWorkingName(newName);
    if (shouldSyncInGameName) {
      setInGameName(newName);
    }

    updateSelectedActor({
      workingName: newName,
      ...(shouldSyncInGameName ? { inGameName: newName } : {}),
    });
  };

  const onChangeInGameName = (newName: string) => {
    setInGameName(newName);
    updateSelectedActor({ inGameName: newName });
  };

  const onChangeInGameNameMode = (newMode: InGameNameMode) => {
    const nextRandomInGameNameType =
      selectedActor?.randomInGameNameType ??
      getDefaultRandomInGameNameType(selectedActor?.gender);
    setInGameNameMode(newMode);
    setRandomInGameNameType(nextRandomInGameNameType);
    updateSelectedActor({
      inGameNameMode: newMode,
      randomInGameNameType: nextRandomInGameNameType,
      ...(newMode === InGameNameMode.manual &&
      !(selectedActor?.inGameName ?? "").trim()
        ? { inGameName: workingName }
        : {}),
    });
  };

  const onChangeRandomInGameNameType = (index: number) => {
    const nextType = RANDOM_IN_GAME_NAME_OPTIONS[index];
    if (!nextType) return;

    setRandomInGameNameType(nextType.value);
    updateSelectedActor({ randomInGameNameType: nextType.value });
  };

  const selectedRandomInGameNameTypeLabel =
    RANDOM_IN_GAME_NAME_OPTIONS.find(
      (option) => option.value === randomInGameNameType
    )?.label ?? "Neutral";

  const onChangeBodyType = (newGender: Gender) => {
    updateSelectedActor({ gender: newGender });
  };

  const header = isQuickAction ? (
    <></>
  ) : (
    <header className="actorpreseteditor-pageheader" data-testid="properties-header">
      <div className="actorpreseteditor-pageheader-icon">
        <BadgeOutlined fontSize="inherit" />
      </div>
      <div className="actorpreseteditor-pageheader-copy">
        <div className="actorpreseteditor-pageheader-eyebrow">Actor preset</div>
        <h1>Editing Actor Preset</h1>
      </div>
    </header>
  );

  const footer = isQuickAction ? (
    <div className="absolute bottom-0 right-0 w-[350px] flex flex-row gap-[10px] h-[40px]">
      <PiButton variant="piRed" onClick={cancelQuickActorModal}>
        Cancel
      </PiButton>
      <PiButton variant="piBlue" onClick={saveQuickActorModal}>
        Save
      </PiButton>
    </div>
  ) : (
    <></>
  );

  return (
    <div className="actorpreseteditor-shell">
      {header}
      <div
        aria-label="actor-preset-window-root"
        className={`actorpreseteditor-scroll pit-creator-scrollbar${
          isQuickAction ? " actorpreseteditor-scroll--quick" : ""
        }`}
      >
        <div
          className={
            isPlayer
              ? "actorpreseteditor-grid actorpreseteditor-grid-player"
              : "actorpreseteditor-grid"
          }
        >
          {!isPlayer && (
            <>
              <ActorPresetEditorField
                name="Identity"
                icon={AccessibilityNewOutlined}
                className="actorpreseteditor-identity"
              >
                <div className="actorpreseteditor-formstack">
                  <div className="actorpreseteditor-formgroup">
                    <div className="actorpreseteditor-fieldlabel">Actor ID</div>
                    <PiInputfield
                      label=""
                      onChange={onChangeWorkingName}
                      placeholder="Working name..."
                      value={workingName}
                    />
                  </div>
                  <div className="actorpreseteditor-formgroup">
                    <div className="actorpreseteditor-inlineheader">
                      <div className="actorpreseteditor-fieldlabel">In-Game Name</div>
                    </div>
                    <PiSegmentedToggle
                      ariaLabel="in-game-name-mode-toggle"
                      className="actorpreseteditor-segment"
                        layoutId="actorpreseteditor-in-game-name-toggle"
                        options={IN_GAME_NAME_MODE_OPTIONS}
                        value={inGameNameMode}
                        onChange={onChangeInGameNameMode}
                    />
                    <AnimatePresence mode="wait" initial={false}>
                      {inGameNameMode === InGameNameMode.manual ? (
                        <motion.div
                          key="in-game-name-manual"
                          initial={{ opacity: 0, y: 8, scale: 0.985 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.985 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                        >
                          <PiInputfield
                            label=""
                            onChange={onChangeInGameName}
                            placeholder="In-game name..."
                            value={inGameName}
                          />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="in-game-name-random"
                          initial={{ opacity: 0, y: 8, scale: 0.985 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.985 }}
                          transition={{ duration: 0.18, ease: "easeOut" }}
                        >
                          <PiCreatorStringDropdown
                            selectedItem={selectedRandomInGameNameTypeLabel}
                            items={RANDOM_IN_GAME_NAME_OPTIONS.map(
                              (option) => option.label
                            )}
                            onClick={(item) => {
                              const index = RANDOM_IN_GAME_NAME_OPTIONS.findIndex(
                                (option) => option.label === item
                              );
                              onChangeRandomInGameNameType(index);
                            }}
                            dropdownClassName="actorpreseteditor-randomdropdown"
                            triggerClassName="actorpreseteditor-randomdropdown-trigger"
                            textClassName="actorpreseteditor-randomdropdown-text"
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="actorpreseteditor-formgroup">
                    <div className="actorpreseteditor-inlineheader">
                      <div className="actorpreseteditor-fieldlabel">Body Type</div>
                    </div>
                    <PiSegmentedToggle
                      ariaLabel="body-type-toggle"
                      className="actorpreseteditor-segment"
                    layoutId="actorpreseteditor-body-type-toggle"
                    options={BODY_TYPE_OPTIONS}
                    value={selectedActor?.gender ?? Gender.random}
                    onChange={onChangeBodyType}
                    />
                  </div>
                </div>
              </ActorPresetEditorField>
              <ActorPresetEditorField
                name="Avatar Color"
                icon={AccountCircleOutlined}
                className="actorpreseteditor-avatar"
                contentClassName="actorpreseteditor-avatarcontent"
              >
                <ActorProfilePictureSelector />
              </ActorPresetEditorField>
              <ActorPresetEditorField
                name="Outfit Configuration"
                icon={CheckroomOutlined}
                className="actorpreseteditor-outfit"
              >
                <ActorOutfitSelector />
              </ActorPresetEditorField>
              <ActorPresetEditorField
                name="Quick Presets"
                icon={FaceOutlined}
                className="actorpreseteditor-presets"
              >
                <ActorCharacterPresetSelector />
              </ActorPresetEditorField>
            </>
          )}
          {isPlayer && (
            <ActorPresetEditorField
              name="Outfit Configuration"
              icon={CheckroomOutlined}
              className="actorpreseteditor-outfit actorpreseteditor-outfit-player"
            >
              <ActorOutfitSelector />
            </ActorPresetEditorField>
          )}
        </div>
      </div>
      {!isQuickAction && <PiSeperator />}
      {footer}
    </div>
  );
}

const RANDOM_IN_GAME_NAME_OPTIONS: Array<{
  label: string;
  value: RandomInGameNameType;
}> = [
  { label: "Neutral", value: RandomInGameNameType.neutral },
  { label: "Male", value: RandomInGameNameType.male },
  { label: "Female", value: RandomInGameNameType.female },
];

const IN_GAME_NAME_MODE_OPTIONS: PiSegmentedToggleOption<InGameNameMode>[] = [
  {
    testId: "in-game-name-mode-manual",
    label: "Manual",
    value: InGameNameMode.manual,
  },
  {
    testId: "in-game-name-mode-random",
    label: "Random",
    value: InGameNameMode.random,
  },
];

const BODY_TYPE_OPTIONS: PiSegmentedToggleOption<Gender>[] = [
  { testId: "body-type-male", label: "Male", value: Gender.male },
  { testId: "body-type-female", label: "Female", value: Gender.female },
  { testId: "body-type-random", label: "Random", value: Gender.random },
];
